// 📊 B242 (AI_110) — ESTATÍSTICA DESCRITIVA (determinística, pura, testável)
// ─────────────────────────────────────────────────────────────
// O CORAÇÃO da regra de ouro do B242: quem CONTA pacientes e CALCULA estatística
// é o CÓDIGO (a IA só propõe o plano e interpreta os números já calculados).
// Por isso estas funções são puras e cobertas por testes — a correção aqui é
// crítica. Nada de inferência avançada no MVP1: só DESCRITIVA sólida.
// ─────────────────────────────────────────────────────────────

// Trava de pequeno número (LGPD / anti-reidentificação). Células com n abaixo
// deste limiar são marcadas; o MASCARAMENTO real (export) endurece no MVP2.
export const N_MINIMO_CELULA = 5;
export function ehNumeroPequeno(n: number): boolean {
  return n > 0 && n < N_MINIMO_CELULA;
}

// Coerção tolerante p/ número: aceita number, ou string "1,5"/"233.000"
// (a normalização forte vem do carregador; aqui é defesa em profundidade).
export function paraNumero(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export interface ResumoNumerico {
  n: number; // observações válidas (numéricas)
  faltantes: number; // total - n
  media: number | null;
  mediana: number | null;
  desvioPadrao: number | null; // amostral (n-1); null se n<2
  min: number | null;
  max: number | null;
  q1: number | null;
  q3: number | null;
}

// Quartil por interpolação linear (método "type 7", padrão do R/NumPy).
// p ∈ [0,1]. Requer array JÁ ORDENADO ascendente e não-vazio.
function quantilOrdenado(ordenados: number[], p: number): number {
  const n = ordenados.length;
  if (n === 1) return ordenados[0];
  const idx = (n - 1) * p;
  const lo = Math.floor(idx);
  const frac = idx - lo;
  if (lo + 1 >= n) return ordenados[n - 1];
  return ordenados[lo] + frac * (ordenados[lo + 1] - ordenados[lo]);
}

export function resumirNumerico(valores: unknown[]): ResumoNumerico {
  const total = valores.length;
  const nums = valores
    .map(paraNumero)
    .filter((v): v is number => v !== null);
  const n = nums.length;
  const faltantes = total - n;

  if (n === 0) {
    return {
      n: 0, faltantes, media: null, mediana: null,
      desvioPadrao: null, min: null, max: null, q1: null, q3: null,
    };
  }

  const ordenados = [...nums].sort((a, b) => a - b);
  const soma = nums.reduce((s, v) => s + v, 0);
  const media = soma / n;
  const mediana = quantilOrdenado(ordenados, 0.5);
  const q1 = quantilOrdenado(ordenados, 0.25);
  const q3 = quantilOrdenado(ordenados, 0.75);
  const min = ordenados[0];
  const max = ordenados[n - 1];

  let desvioPadrao: number | null = null;
  if (n >= 2) {
    const variancia =
      nums.reduce((s, v) => s + (v - media) * (v - media), 0) / (n - 1);
    desvioPadrao = Math.sqrt(variancia);
  }

  return { n, faltantes, media, mediana, desvioPadrao, min, max, q1, q3 };
}

export interface ContagemCategoria {
  valor: string;
  n: number;
  proporcao: number; // sobre o total de NÃO-faltantes (0..1)
  pequeno: boolean; // n < N_MINIMO_CELULA
}
export interface ResumoCategorico {
  n: number; // não-faltantes
  faltantes: number;
  categorias: ContagemCategoria[]; // ordenadas por n desc
}

// Categórico/booleano. Trata null/undefined/"" como faltante. Booleanos viram
// "Sim"/"Não". Demais valores são convertidos para string (trim).
export function resumirCategorico(valores: unknown[]): ResumoCategorico {
  const total = valores.length;
  const contagem = new Map<string, number>();
  let faltantes = 0;

  for (const v of valores) {
    if (v === null || v === undefined || v === "") {
      faltantes++;
      continue;
    }
    const chave =
      typeof v === "boolean" ? (v ? "Sim" : "Não") : String(v).trim();
    if (chave === "") {
      faltantes++;
      continue;
    }
    contagem.set(chave, (contagem.get(chave) || 0) + 1);
  }

  const n = total - faltantes;
  const categorias: ContagemCategoria[] = Array.from(contagem.entries())
    .map(([valor, c]) => ({
      valor,
      n: c,
      proporcao: n > 0 ? c / n : 0,
      pequeno: ehNumeroPequeno(c),
    }))
    .sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor));

  return { n, faltantes, categorias };
}
